import { getApiError } from '../utils/errorHelper';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Mail, KeyRound, ArrowLeft, CheckSquare, Square, Shield } from 'lucide-react';
import { companyInfo } from '../data/mock';
import api from '../services/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();
  // Deep-link redirect: after successful login, land on the target URL from ?next=... (if same-origin)
  const nextParam = (() => {
    try {
      const q = new URLSearchParams(location.search).get('next');
      if (!q) return null;
      const decoded = decodeURIComponent(q);
      // Only allow same-origin relative paths starting with '/'
      if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
      return null;
    } catch { return null; }
  })();
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the Terms & Conditions');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { email: email.toLowerCase() });
      toast.success('OTP sent to your email. Please check your inbox and spam folder.');
      setStep('otp');
    } catch (error) {
      toast.error(getApiError(error, 'Failed to send OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-otp', { 
        email: email.toLowerCase(), 
        otp 
      });
      
      const { access_token, user } = response.data;
      
      // Update auth context (this will also store in localStorage)
      setAuthUser(user, access_token);
      
      // Accept terms after login
      try {
        await api.post('/auth/accept-terms', {}, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
      } catch (e) {
        // Silent fail - terms acceptance is secondary
      }
      
      toast.success('Login successful!');
      
      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        // If a deep-link target was passed (e.g., ?next=/?section=tickets&ticket=abc), honour it
        if (nextParam) {
          navigate(nextParam, { replace: true });
          return;
        }
        // Redirect based on role
        if (user.role === 'super_admin' || user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 100);
    } catch (error) {
      toast.error(getApiError(error, 'Invalid OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { email: email.toLowerCase() });
      toast.success('New OTP sent to your email');
    } catch (error) {
      toast.error(getApiError(error, 'Failed to resend OTP'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#080b09] via-[#0f1611] to-[#080b09] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/[0.08] rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-700/[0.06] rounded-full blur-[120px]" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.name}
              className="w-14 h-14 object-contain"
            />
            <div className="text-left">
              <h1 className="text-xl font-bold text-white">{companyInfo.name}</h1>
              <p className="text-slate-400 text-sm">Chartered Accountants</p>
            </div>
          </Link>
        </div>

        {/* Login Card */}
        <div className="bg-[#0f1611]/80 backdrop-blur-lg rounded-2xl p-8 border border-emerald-900/40 shadow-xl">
          {step === 'email' ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Welcome</h2>
                <p className="text-slate-400 text-sm">Enter your email to receive a one-time password</p>
              </div>

              <form onSubmit={handleRequestOTP} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-[#080b09] border-emerald-900/30 text-white placeholder:text-slate-500 focus-visible:ring-emerald-500"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                {/* Terms & Conditions Checkbox */}
                <div 
                  className="flex items-start gap-3 cursor-pointer group"
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  data-testid="login-terms-checkbox"
                >
                  <div className="mt-0.5">
                    {acceptedTerms ? (
                      <CheckSquare className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400 group-hover:text-slate-300" />
                    )}
                  </div>
                  <p className="text-sm text-slate-400">
                    I agree to the{' '}
                    <Link 
                      to="/terms" 
                      target="_blank"
                      className="text-emerald-400 hover:text-emerald-300 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms & Conditions
                    </Link>
                    {' '}and{' '}
                    <Link 
                      to="/privacy" 
                      target="_blank"
                      className="text-emerald-400 hover:text-emerald-300 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || !acceptedTerms}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 disabled:opacity-50"
                  data-testid="login-request-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Get OTP'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Enter OTP</h2>
                <p className="text-slate-400 text-sm">
                  We&apos;ve sent a 6-digit code to<br />
                  <span className="text-emerald-400 font-medium">{email}</span>
                </p>
                <p className="text-amber-400 text-xs mt-2">
                  Please check your spam/junk folder if you don&apos;t see the email
                </p>
              </div>

              <form onSubmit={handleVerifyOTP} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">One-Time Password</label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    className="bg-[#080b09] border-emerald-900/30 text-white placeholder:text-slate-500 text-center text-2xl tracking-[0.5em] font-mono focus-visible:ring-emerald-500"
                    maxLength={6}
                    required
                    data-testid="login-otp-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3"
                  data-testid="login-verify-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button 
                    type="button"
                    onClick={() => setStep('email')}
                    className="text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Change email
                  </button>
                  <button 
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-emerald-900/30 text-center">
            <p className="text-slate-400 text-sm">
              Don&apos;t have an account?{' '}
              <span className="text-emerald-400">
                Enter your email above to create one automatically
              </span>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-slate-400 hover:text-white text-sm transition-colors inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
        
        {/* Security Badge */}
        <div className="mt-8 pt-4 border-t border-emerald-900/30">
          <div className="flex items-center justify-center gap-3 text-slate-500 text-xs">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span>ISO/IEC 27001 Certified • Your data is protected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
